<script lang="ts">
import type { Component } from 'vue'

export type AvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'xl'

export interface AvatarProps {
  as?: string | Component
  src?: string
  alt?: string
  icon?: string
  iconClass?: string
  text: string
  size?: AvatarSize
  class?: string | object
  style?: string | object
  isRounded?: boolean
}

export interface AvatarSlots {
  default: (props?: object) => any
}

export function stringToHslColor(str: string, s: number, l: number) {
  let hash = 0
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  const h = Math.abs(hash % 360)
  return `hsl(${h}, ${s}%, ${l}%)`
}

const ui = {
  root: 'inline-flex items-center justify-center shrink-0 select-none align-middle',
  image: 'h-full w-full rounded-[inherit] object-cover',
  label: 'text-white leading-none text-muted truncate uppercase',
  icon: 'text-purple text-muted shrink-0',
  color: 'bg-{color}-600',
  sizes: {
    xxs: {
      rounded: 'rounded-md',
      root: 'text-[0.6rem] font-bold size-18px',
      icon: 'size-3',
    },
    xs: {
      rounded: 'rounded-md',
      root: 'text-xs font-bold size-22px',
      icon: 'size-3.5',
    },
    sm: {
      rounded: 'rounded-md',
      root: 'text-sm font-bold size-25px',
      icon: 'size-4',
    },
    md: {
      rounded: 'rounded-md',
      root: 'text-md font-bold size-28px',
      icon: 'size-4.5',
    },
    xl: {
      rounded: 'rounded-lg',
      root: 'text-md font-bold size-36px',
      icon: 'size-6',
    },
  },
}
</script>

<script setup lang="ts">
import { Primitive, Slot } from 'reka-ui'
import { computed, ref, watch } from 'vue'
import AwIcon from './AwIcon.vue'

defineOptions({ inheritAttrs: false })

const {
  as = 'span',
  src,
  alt,
  icon,
  text,
  size = 'md',
  class: propsClass,
  style,
  isRounded = true,
} = defineProps<AvatarProps>()

const label = computed(() => text.split(' ').map(word => word.charAt(0)).join('').substring(0, 2))
const color = computed(() => stringToHslColor(text, 100, 40))
const sizeClass = computed(() => ui.sizes[size].root)
const rootClass = computed(() => [
  ui.root,
  isRounded ? 'rounded-full' : ui.sizes[size].rounded,
  sizeClass.value,
  propsClass,
])
const sizePx = computed(() => ({ xxs: 16, xs: 20, sm: 25, md: 28, xl: 36 })[size])

const hasError = ref(false)

watch(() => src, () => {
  if (hasError.value)
    hasError.value = false
})

function onError() {
  hasError.value = true
}
</script>

<template>
  <Primitive
    :as
    :style="[
      style,
      !rootClass.find(el => typeof el === 'string' && el.includes('bg-')) && { backgroundColor: color },
    ]"
    :class="rootClass"
  >
    <img
      v-if="src && !hasError"
      role="img"
      :src :alt :width="sizePx" :height="sizePx"
      v-bind="$attrs"
      :class="ui.image"
      @error="onError"
    >
    <Slot v-else v-bind="$attrs">
      <slot>
        <AwIcon v-if="icon" :name="icon" :class="[ui.icon, ui.sizes[size].icon, iconClass ?? '']" />
        <span v-else :class="ui.label">{{ label }}</span>
      </slot>
    </Slot>
  </Primitive>
</template>
